import type { FC, MouseEvent, KeyboardEvent } from 'react';
import React from 'react';
import { graphql } from 'react-relay';
import { defineMessages, FormattedMessage } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import DropdownItem from '@atlaskit/dropdown-menu/dropdown-menu-item';

// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { ErrorBoundary } from '@confluence/error-boundary/entry-points/ErrorBoundary.ts';
import { ShortcutVisualizer } from '@confluence/shortcuts/entry-points/ShortcutVisualizer.ts';
import { PageStar, StarIcon } from '@confluence/action-buttons/entry-points/pageStar.ts';

import { getStarShortcutString } from './FavouriteContentShortcutListener.tsx';
import { useFavouriteContentFragment } from './useFavouriteContentFragment.ts';
import type {
	FavouriteMenuItemQuery$data,
	FavouriteMenuItemQuery$variables,
} from './__generated__/FavouriteMenuItemQuery.graphql.ts';

type FavouriteMenuItemProps = {
	contentId: string;
	contentTitle: string;
	contentMode: string;
};

// This component is a dropdown menu item version of the favorite page star button. It reads the editor fragment when provided and otherwise queries its own favourite data.
// It does not include any shortcut listener and should be used in conjunction with FavouriteContentShortcutListener.tsx if you need to add a listener.
// It is currently used in the page header more actions menu item
const FavouriteMenuItemQuery = graphql`
	query FavouriteMenuItemQuery($contentId: ID!) {
		singleContent(id: $contentId) {
			id
			type
			metadata {
				currentuser {
					favourited {
						isFavourite
					}
				}
			}
		}
		contentOperations: singleContent(id: $contentId) {
			id
			operations {
				operation
				targetType
			}
		}
	}
`;

export const FavouriteMenuItem: FC<FavouriteMenuItemProps> = ({
	contentId,
	contentTitle,
	contentMode,
}) => {
	const { isLoggedIn } = useSessionData();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const editorFragmentResult = useFavouriteContentFragment({ contentId });
	const queryResult = useQueryInterop<
		FavouriteMenuItemQuery$data,
		FavouriteMenuItemQuery$variables
	>(FavouriteMenuItemQuery, {
		variables: {
			contentId,
		},
		fetchPolicy: 'cache-first',
		skip: editorFragmentResult.isProvided,
	});
	const { data, error } = editorFragmentResult.isProvided ? editorFragmentResult : queryResult;

	if (error) {
		return <ErrorDisplay error={error} />;
	}

	const isStarred = Boolean(data?.singleContent?.metadata.currentuser?.favourited?.isFavourite);
	const contentType = data?.singleContent?.type;
	const shortcut = getStarShortcutString(contentType || '', contentMode);
	const handleClick = (toggleStar) => (e: MouseEvent<Element> | KeyboardEvent<Element>) => {
		toggleStar(e);
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'pageStar',
				attributes: {
					triggeredByKeyboardShortcut: false,
					action: isStarred ? 'unstar' : 'star',
					isInEditor: contentMode === 'live' || contentMode === 'edit' || contentMode === 'draft',
					isContentWrapper: true,
				},
				source: 'FavouriteMoreActionsMenuItem',
			},
		}).fire();
	};

	return (
		<ErrorBoundary attribution={Attribution.DISCO}>
			<PageStar
				contentId={contentId}
				isStarred={isStarred}
				contentTitle={contentTitle}
				analytics={{
					source: 'FavouriteMoreActionsMenuItem',
					attributes: {
						componentVersion: 'more-actions-menu-item',
						pageType: contentType,
					},
				}}
				shouldShowFlags
			>
				{({ toggle: toggleStar }) => (
					<DropdownItem
						onClick={handleClick(toggleStar)}
						elemBefore={<StarIcon isStarred={isStarred} spacing="none" />}
						elemAfter={shortcut && <ShortcutVisualizer shortcut={shortcut} />}
						isDisabled={!isLoggedIn}
					>
						{isStarred ? (
							<FormattedMessage {...i18n.unstar} />
						) : (
							<FormattedMessage {...i18n.star} />
						)}
					</DropdownItem>
				)}
			</PageStar>
		</ErrorBoundary>
	);
};

const i18n = defineMessages({
	star: {
		id: 'favourite-button.content.star.label',
		defaultMessage: 'Star',
		description:
			'Label for unfilled star menu item which indicates that the content is unstarred & stars the content when clicked',
	},
	unstar: {
		id: 'favourite-button.content.unstar.label',
		defaultMessage: 'Unstar',
		description:
			'Label for filled star menu item which indicates that the content is unstarred & stars the content when clicked',
	},
});
